import React from 'react';

/**
 * FuturisticSlider — stacked design: label above, full-width slider below
 */
export default function FuturisticSlider({
  min, max, step = 1, value, onChange,
  label, labelWidth = 'auto', onReset, subtext,
  warn = false,
  remainingPct = 100,
}) {
  const pct = max === min ? 0 : Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

  const isWarned = warn || remainingPct < 15;
  const isAlert  = remainingPct < 30 && !isWarned;

  const trackColor = isWarned
    ? 'linear-gradient(90deg, #ef4444, #dc2626)'
    : isAlert
    ? 'linear-gradient(90deg, #f59e0b, #d97706)'
    : 'linear-gradient(90deg, #7c3aed, #a78bfa)';

  const glowColor = isWarned
    ? 'rgba(239,68,68,0.55)'
    : isAlert
    ? 'rgba(245,158,11,0.45)'
    : 'rgba(139,92,246,0.55)';

  const badgeColor = isWarned ? '#ef4444' : isAlert ? '#f59e0b' : '#a78bfa';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', width: '100%' }}>
      {/* ── Label (Left) ── */}
      {label && (
        <div style={{
          width: labelWidth,
          maxWidth: 120,
          flexShrink: 0,
          fontSize: 11, fontWeight: 600,
          color: 'var(--t2)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {label}
        </div>
      )}

      {/* ── Slider Track (Center) ── */}
      <div style={{ flex: 1, position: 'relative', height: 18, display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {/* Background track */}
        <div style={{
          position: 'absolute', left: 0, right: 0,
          height: 4, borderRadius: 99,
          background: 'rgba(255,255,255,0.07)',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 99,
            background: trackColor,
            boxShadow: `0 0 8px ${glowColor}`,
            transition: 'width 0.15s ease, background 0.3s ease, box-shadow 0.3s ease',
          }} />
        </div>

        {/* Native range input (invisible overlay) */}
        <input
          type="range"
          min={min} max={max} step={step}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            opacity: 0, cursor: 'pointer',
            margin: 0, padding: 0,
          }}
        />

        {/* Custom thumb dot */}
        <div style={{
          position: 'absolute',
          left: `calc(${pct}% - 7px)`,
          width: 14, height: 14,
          borderRadius: '50%',
          background: isWarned ? '#ef4444' : isAlert ? '#f59e0b' : '#a78bfa',
          boxShadow: `0 0 0 3px rgba(0,0,0,0.6), 0 0 10px ${glowColor}`,
          border: '2px solid rgba(255,255,255,0.2)',
          transition: 'left 0.15s ease, background 0.3s ease',
          pointerEvents: 'none',
        }} />
      </div>

      {/* ── Value Badge & Reset (Right) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        <span style={{
          fontSize: 11, fontWeight: 800,
          color: badgeColor,
          background: `${badgeColor}18`,
          border: `1px solid ${badgeColor}40`,
          borderRadius: 99,
          padding: '1px 0',
          width: subtext ? 70 : 42, minWidth: subtext ? 70 : 42, maxWidth: subtext ? 70 : 42,
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
          fontFamily: 'monospace',
          transition: 'color 0.3s, border-color 0.3s, background 0.3s',
          whiteSpace: 'nowrap',
          display: 'inline-block',
          flexShrink: 0,
        }}>
          {value}{subtext ? <span style={{ fontWeight: 500, opacity: 0.7, marginLeft: 4 }}>{subtext}</span> : null}
        </span>

        {onReset && (
          <button
            onClick={onReset}
            title="Reset"
            style={{
              width: 20, height: 20, borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.04)',
              color: 'var(--t3)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s', flexShrink: 0, padding: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--t3)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
